import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { deviceService } from '@/api/domains/device';
import type { LC_GetDeviceSummaryOut } from '@/api/domains/device';
import { useUI } from '@/hooks/ui/useUI';
import { useAbortController } from '@/hooks/ui/useAbortController';


export interface UseDeviceSummaryResult {
  totals: LC_GetDeviceSummaryOut;
  isLoading: boolean;
}

const INITIAL_TOTALS: LC_GetDeviceSummaryOut = {
  total: 0,
  connected: 0,
  disconnected: 0,
};

export function useDeviceSummary(): UseDeviceSummaryResult {
  const { t: translate } = useTranslation('device');
  const { showApiError } = useUI();
  const { createSignal } = useAbortController();

  const [totals, setTotals] = useState<LC_GetDeviceSummaryOut>(INITIAL_TOTALS);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const signal = createSignal();

    const loadSummary = async () => {
      setIsLoading(true);

      const response = await deviceService.getSummary(signal);

      if (response.error?.code === 'ABORTED') {
        setIsLoading(false);
        return;
      }

      if (!response.success || !response.data) {
        showApiError(response, translate('errors.loadFailed'));
        setTotals(INITIAL_TOTALS);
        setIsLoading(false);
        return;
      }

      setTotals({
        total: response.data.total,
        connected: response.data.connected,
        disconnected: response.data.disconnected,
      });
      setIsLoading(false);
    };

    void loadSummary();
    // Fetch único al montar: no debe re-dispararse por cambios de identidad de
    // translate/showApiError, ya que no hay dependencias reales de esos valores en este efecto.
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return { totals, isLoading };
}

export default useDeviceSummary;
